import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Stack,
  HookForm,
  Typography,
  Button,
  Drawer,
  Divider,
  IconButton,
  useNiceModal,
  Link,
} from '@castlery/fortress';
import { form, validators, termsLink, privacyLink } from './helper';
import { useAsyncFn } from 'react-use';
import { useAppDispatch, useAppSelector } from '@castlery/shared-redux-store';
import { createCustomerFromPosChannel, selectedCurrentCustomer } from '@castlery/modules-user-domain';
import { AddAccount } from '@castlery/fortress/Icons';
import { AddAddressForm } from '@castlery/shared-components';
import { addAddressForNewCustomerCommand } from '@castlery/modules-checkout-services';
import { SearchAddress, type SearchAddressValue } from '@castlery/shared-components';

interface Payload {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
}
export interface AddNewCustomerFormProps {
  onSubmit: (data: Payload) => Promise<any>;
  onCancel: () => void;
}
export function AddNewCustomerForm({ onSubmit, onCancel }: AddNewCustomerFormProps) {
  const [addState, handleAdd] = useAsyncFn(async (data: Payload) => {
    return await onSubmit(data);
  }, []);

  const errorMessage = useMemo(
    () => (addState.loading ? '' : addState.error ? addState.error?.data?.errors?.[0]?.detail : ''),
    [addState]
  );

  return (
    <>
      <Stack sx={{ paddingY: 3, gap: 4 }}>
        <HookForm
          form={form}
          validators={validators}
          submit={handleAdd}
          formSxProps={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Typography level="caption1" color="danger" sx={{ marginY: 3 }}>
            {errorMessage}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 4,
            }}
          >
            <Button variant="tertiary" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" loading={addState.loading}>
              Add Customer
            </Button>
          </Box>
        </HookForm>

        <Divider />
        <Typography level="caption1">
          Before clicking the Add button, please make sure customers agree to Castlery's{' '}
          <Link underline="always" rel="noopener" target="_blank" href={termsLink}>
            Terms of Use
          </Link>{' '}
          and{' '}
          <Link underline="always" rel="noopener" target="_blank" href={privacyLink}>
            Privacy Policy
          </Link>
          . They will be opted in to our newsletters, and can unsubscribe at anytime.
        </Typography>
      </Stack>
    </>
  );
}

export interface PosAddNewCustomerProps {
  iconButton?: boolean;
  afterAddCustomer?: () => void;
  defaultStep?: 'Add New Customer' | 'Select Address' | 'Complete Your Address';
}

export function PosAddNewCustomer({
  iconButton = false,
  afterAddCustomer,
  defaultStep = 'Complete Your Address',
}: PosAddNewCustomerProps) {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'Add New Customer' | 'Select Address' | 'Complete Your Address'>(defaultStep);
  const [modal, contextHolder] = useNiceModal();
  const customer = useAppSelector(selectedCurrentCustomer);

  const handleAddCustomer = useCallback(
    async (data: Payload) => {
      await dispatch(createCustomerFromPosChannel.initiate(data)).unwrap();
      await setStep('Select Address');
    },
    [dispatch]
  );

  const onCancelOnCompleteYourAddress = () => {
    setStep('Select Address');
  };
  const handleSubmitOnCompleteYourAddress = async (data: Record<string, any>) => {
    if (!customer?.id) {
      return Promise.reject('No customer id');
    }
    await dispatch(
      addAddressForNewCustomerCommand({
        address: data,
        uid: customer?.id,
      })
    ).unwrap();
    setOpen(false);
    afterAddCustomer && afterAddCustomer();
  };
  const [addressOptions, setAddressOptions] = useState<SearchAddressValue | null>(null);

  return (
    <React.Fragment>
      {iconButton ? (
        !customer && (
          <IconButton sx={{ minHeight: 24, minWidth: 24, p: 0 }} onClick={() => setOpen(true)}>
            <AddAccount />
          </IconButton>
        )
      ) : (
        <Button
          fullWidth
          variant="primary"
          onClick={() => {
            setOpen(true);
            setStep('Add New Customer');
          }}
        >
          New Customer
        </Button>
      )}

      <Drawer
        title={step}
        open={open}
        showCloseButton
        onClose={(_event: React.MouseEvent<HTMLButtonElement>, reason: string) => {
          if (step === 'Complete Your Address') {
            modal.warning({
              title: 'You are about to cancel the address creation',
              confirmText: 'Stay',
              cancelText: 'Leave',
              onCancel() {
                setOpen(false);
              },
            });
            return;
          }
          setOpen(false);
          afterAddCustomer && afterAddCustomer();
        }}
      >
        {step === 'Add New Customer' && (
          <AddNewCustomerForm
            onSubmit={handleAddCustomer}
            onCancel={() => {
              setOpen(false);
            }}
          />
        )}
        {step === 'Select Address' && (
          <SearchAddress
            onChange={(value) => {
              setAddressOptions(value);
              if (value) {
                setStep('Complete Your Address');
              }
            }}
            onClick={() => {
              setStep('Complete Your Address');
            }}
          />
        )}
        {step === 'Complete Your Address' && (
          <AddAddressForm
            defaultCustomer={customer}
            defaultAddress={addressOptions}
            cancel={onCancelOnCompleteYourAddress}
            submit={handleSubmitOnCompleteYourAddress}
          />
        )}
      </Drawer>
      {contextHolder}
    </React.Fragment>
  );
}

export default PosAddNewCustomer;
